import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  styled,
  Grid
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import { alpha } from '@mui/material/styles';

// This would come from your API
interface Integration {
  id: string;
  name: string;
  type: string;
  status: 'active' | 'inactive';
  logoUrl: string;
  description: string;
  credentials: {
    [key: string]: string;
  };
}

// Voeg deze interface toe bij de andere interfaces
interface AvailableIntegration {
  id: string;
  name: string;
  type: string;
  logoUrl: string;
  description: string;
}

const Integrations: React.FC = () => {
  const { t } = useTranslation();
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null);
  // Nieuwe state voor beschikbare integraties
  const [availableIntegrations] = useState<AvailableIntegration[]>([
    {
      id: '1',
      name: 'GoTickets',
      type: 'External Ticket System',
      logoUrl: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRS_VHMv4MqDMQ7d1mI-FR3h0cFQaf0KmNryA&s',
      description: 'Connect your GoTickets account to the Entrada Security system'
    },
    {
      id: '2',
      name: 'Jira',
      type: 'Project Management',
      logoUrl: 'https://cdn.worldvectorlogo.com/logos/jira-1.svg',
      description: 'Connect with Jira for project and ticket management'
    }
    // Voeg hier meer beschikbare integraties toe
  ]);

  // Mock data - replace with API call
  useEffect(() => {
    // Simulated API response
    setIntegrations([
      {
        id: '0',
        name: 'GoTickets',
        type: 'External Ticket System',
        status: 'inactive',
        logoUrl: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRS_VHMv4MqDMQ7d1mI-FR3h0cFQaf0KmNryA&s',
        description: 'Connect your GoTickets account to the Entrada Security system',
        credentials: {
          apiKey: 'xxxxx-xxxxx'
        }
      }
    ]);
  }, []);

  const handleAddIntegration = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedIntegration(null);
  };

  // Nieuwe functie om een integratie te selecteren
  const handleSelectIntegration = (integration: AvailableIntegration) => {
    setSelectedIntegration({
      ...integration,
      status: 'inactive',
      credentials: {}
    } as Integration);
  };

  return (
    <Wrapper>
      <Header>
        <Typography variant="h5">INTEGRATIONS</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
          sx={{
            backgroundColor: '#9CC740',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: '#8BB534'
            }
          }}>
          Add integration
        </Button>
      </Header>

      <Content>
        <Grid
          container
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 3
          }}>
          {integrations.map(integration => (
            <Grid item key={integration.id}>
              <IntegrationCard>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <IntegrationLogo src={integration.logoUrl} alt={integration.name} />
                    <Box sx={{ ml: 2, flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 500 }}>
                        {integration.name}
                      </Typography>
                      <StatusBadge status={integration.status}>{integration.status}</StatusBadge>
                    </Box>
                  </Box>
                  <Typography color="text.secondary" sx={{ mb: 3 }}>
                    {integration.description}
                  </Typography>
                  <Button
                    fullWidth
                    variant={integration.status === 'active' ? 'outlined' : 'contained'}
                    sx={{
                      borderRadius: '4px',
                      backgroundColor: integration.status === 'inactive' ? '#9CC740' : 'transparent',
                      borderColor: '#9CC740',
                      color: integration.status === 'inactive' ? 'white' : '#9CC740',
                      '&:hover': {
                        backgroundColor: integration.status === 'inactive' ? '#8BB534' : 'rgba(156, 199, 64, 0.04)',
                        borderColor: '#8BB534'
                      }
                    }}>
                    {integration.status === 'active' ? 'Configure' : 'Activate'}
                  </Button>
                </CardContent>
              </IntegrationCard>
            </Grid>
          ))}
        </Grid>
      </Content>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{selectedIntegration ? 'Configure Integration' : 'Available Integrations'}</DialogTitle>
        <DialogContent>
          {!selectedIntegration ? (
            <Grid container spacing={2}>
              {availableIntegrations.map(integration => (
                <Grid item xs={12} key={integration.id}>
                  <Card sx={{ cursor: 'pointer' }} onClick={() => handleSelectIntegration(integration)}>
                    <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                      <IntegrationLogo src={integration.logoUrl} alt={integration.name} />
                      <Box sx={{ ml: 2 }}>
                        <Typography variant="h6">{integration.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {integration.description}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Configure {selectedIntegration.name} integration
              </Typography>
              <TextField fullWidth label="API Key" margin="normal" />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          {selectedIntegration && (
            <Button
              variant="contained"
              color="primary"
              sx={{
                backgroundColor: '#9CC740',
                '&:hover': {
                  backgroundColor: '#8BB534'
                }
              }}>
              Add Integration
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Wrapper>
  );
};

const Wrapper = styled(Box)`
  height: 100%;
  background-color: #fff;
  overflow: auto;
  display: flex;
  flex-direction: column;
`;

const Header = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing(2, 3)};
  border-bottom: 1px solid ${({ theme }) => theme.palette.divider};
  background-color: #fff;
  position: sticky;
  top: 0;
  z-index: 1;
`;

const Content = styled(Box)`
  padding: ${({ theme }) => theme.spacing(3, 6)};
  flex: 1;
  min-height: 0;
`;

const IntegrationCard = styled(Card)`
  height: 100%;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.palette.divider};
  box-shadow: none;
`;

const IntegrationLogo = styled('img')`
  width: 40px;
  height: 40px;
  object-fit: contain;
`;

const StatusBadge = styled('span')<{ status: string }>`
  display: inline-block;
  padding: ${({ theme }) => theme.spacing(0.5, 1)};
  border-radius: 4px;
  font-size: 12px;
  text-transform: capitalize;
  background-color: ${({ status }) => (status === 'active' ? 'rgba(156, 199, 64, 0.1)' : '#f5f5f5')};
  color: ${({ status }) => (status === 'active' ? '#9CC740' : '#666')};
`;

export default Integrations;
